/**
 * Location confirmation popup – show on every booking/add-to-cart.
 * Location is taken from the product (via product-location-data from shop metafield), not global header.
 * On confirm: update header location if product location differs, then add to cart; popup closes after response, drawer opens.
 */
(function () {
  const POPUP_ID = 'location-confirmation-popup';
  const SESSION_KEY = 'location_confirmed_location';

  // Plan keys and titles come from window.MembershipPlans — the one registry. The literal is
  // only a degrade path for a page where the registry failed to load. Legacy keys are included
  // so an explicit plan key still resolves to a title, even though the plan is unbuyable.
  const PLAN_TITLES = (() => {
    const registry = typeof window !== 'undefined' ? window.MembershipPlans : null;
    if (registry && typeof registry.sellableKeys === 'function') {
      const keys = registry.sellableKeys().concat(
        typeof registry.legacyKeys === 'function' ? registry.legacyKeys() : []
      );
      return keys.reduce((titles, key) => {
        titles[key] = registry.titleFor({ type: key });
        return titles;
      }, {});
    }
    return { starter: 'Starter', pro: 'Pro', elite: 'Elite', executive: 'Executive', unlimited: 'Unlimited' };
  })();
  const PLAN_KEYS = Object.keys(PLAN_TITLES);
  const DURATION_TITLES = {
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    annual: 'Annually'
  };

  class LocationConfirmationPopup {
    // ─── Storage & location helpers (static) ───────────────────────────────────

    static getCurrentLocation() {
      return (
        (typeof localStorage !== 'undefined' && localStorage.getItem('location')) ||
        (typeof localStorage !== 'undefined' && localStorage.getItem('selected_facility')) ||
        ''
      ).trim();
    }

    static getConfirmedLocation() {
      try {
        return (sessionStorage.getItem(SESSION_KEY) || '').trim();
      } catch (e) {
        return '';
      }
    }

    static setConfirmedLocation(location) {
      try {
        if (location) sessionStorage.setItem(SESSION_KEY, location);
      } catch (e) {}
    }

    static clearConfirmedLocation() {
      try {
        sessionStorage.removeItem(SESSION_KEY);
      } catch (e) {}
    }

    /**
     * Product location from page (#product-location-data from product-location-from-metafield.liquid).
     * @returns {{ location_display: string, city_state: string, facility: string } | null}
     */
    static getProductLocationFromPage() {
      const el = document.getElementById('product-location-data');
      if (!el || !el.textContent) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    // ─── DOM & UI helpers ─────────────────────────────────────────────────────

    getElements() {
      const popup = document.getElementById(POPUP_ID);
      if (!popup) return null;
      return {
        popup,
        locationValue: popup.querySelector('[data-location-value]'),
        locationRow: popup.querySelector('[data-location-row]'),
        facilityCard: popup.querySelector('[data-facility-card]'),
        facilityPhoto: popup.querySelector('[data-facility-photo]'),
        facilityName: popup.querySelector('[data-facility-name]'),
        facilityAddress: popup.querySelector('[data-facility-address]'),
        when: popup.querySelector('[data-when]'),
        whenMeta: popup.querySelector('[data-when-meta]'),
        whenTime: popup.querySelector('[data-when-time]'),
        popupTitle: popup.querySelector('[data-popup-title]'),
        extraRow: document.getElementById('location-confirmation-extra-row'),
        extraLabel: popup.querySelector('.location-confirmation-popup__extra-label'),
        extraValue: popup.querySelector('.location-confirmation-popup__extra-value'),
        confirmBtn: popup.querySelector('[data-location-confirm]'),
        cancelBtn: popup.querySelector('.location-confirmation-popup__cancel'),
        loadingEl: document.getElementById('location-confirmation-loading'),
        actionsEl: document.getElementById('location-confirmation-actions')
      };
    }

    showLoading(els) {
      if (!els) return;
      if (els.loadingEl) {
        els.loadingEl.hidden = false;
        els.loadingEl.setAttribute('aria-hidden', 'false');
      }
      if (els.actionsEl) els.actionsEl.setAttribute('aria-hidden', 'true');
      if (els.confirmBtn) {
        els.confirmBtn.disabled = true;
        els.confirmBtn.setAttribute('aria-busy', 'true');
      }
      if (els.cancelBtn) els.cancelBtn.disabled = true;
    }

    hideLoading(els) {
      if (!els) return;
      if (els.loadingEl) {
        els.loadingEl.hidden = true;
        els.loadingEl.setAttribute('aria-hidden', 'true');
      }
      if (els.actionsEl) els.actionsEl.removeAttribute('aria-hidden');
      if (els.confirmBtn) {
        els.confirmBtn.disabled = false;
        els.confirmBtn.removeAttribute('aria-busy');
      }
      if (els.cancelBtn) els.cancelBtn.disabled = false;
    }

    hidePopup() {
      if (typeof window.hideModal === 'function') {
        window.hideModal(POPUP_ID);
      }
    }

    showPopup() {
      if (typeof window.showModal === 'function') {
        window.showModal(POPUP_ID);
      }
    }

    // ─── Plan display formatting ─────────────────────────────────────────────
    // Detects billing duration (annual → quarterly → monthly) then plan tier.
    // Option values may be e.g. "Pro Monthly", "Unlimited Annually", "Quarterly".
    // `planKey` (resolved from the product's `a9os_id`) wins over the name scan: a selling plan
    // named only for its term carries no tier, which left Starter/Elite/Executive bare.

    static formatMembershipPlanDisplay(planName, planKey) {
      const explicitTier = String(planKey || '').trim().toLowerCase();
      if (!planName || typeof planName !== 'string') {
        return PLAN_TITLES[explicitTier] || '';
      }
      const s = (window.locationConfirmationStrings || {});
      const lower = planName.toLowerCase().trim();

      const isAnnual = lower.includes('annual') || lower.includes('yearly') || lower === 'year' ||
        lower.includes('12 month') || lower.includes('12-month') || (lower.includes('12') && lower.includes('month')) ||
        (lower.includes('year') && !lower.includes('month'));
      const isQuarterly = lower.includes('quarter') || lower.includes('3 month') || lower.includes('3-month');
      const isMonthly = lower.includes('month'); // "12 month" / "3 month" already counted above

      // Tier comes from the plan list, so a new backend plan needs no branch.
      // Whole words only: a substring test resolved "Elite Promo" to `pro`, since "promo"
      // contains it. Longest key first, so a future `pro_plus` cannot lose to `pro`.
      const words = lower.split(/[^a-z_]+/).filter(Boolean);
      const tier = (PLAN_TITLES[explicitTier] ? explicitTier : null) ||
        [...PLAN_KEYS].sort((a, b) => b.length - a.length)
          .find((candidate) => words.includes(candidate)) || null;
      const duration = isAnnual ? 'annual' : isQuarterly ? 'quarterly' : isMonthly ? 'monthly' : null;

      if (!duration && !tier) return planName.trim();

      const key = tier && duration ? 'plan_' + tier + '_' + duration : null;
      if (key && s[key]) return s[key];
      if (tier && duration) return PLAN_TITLES[tier] + ', ' + DURATION_TITLES[duration];
      if (tier) return PLAN_TITLES[tier];
      return planName.trim();
    }

    /**
     * "Another Nine - Steele Creek", the street address and the facility photo, from the page's
     * #product-location-data. Used only when it describes the facility being confirmed (same
     * address1, or the caller named none); otherwise the plain Location row stays.
     */
    fillFacilityCard(els, data) {
      if (!els.facilityCard) return;
      const page = LocationConfirmationPopup.getProductLocationFromPage();
      const sameFacility = page && page.facility_name &&
        (!data.facility || String(data.facility).trim().toLowerCase() === String(page.facility || '').trim().toLowerCase());
      const info = sameFacility ? page : null;

      els.facilityCard.hidden = !info;
      if (els.locationRow) els.locationRow.hidden = !!info;
      if (!info) return;

      // Two spans so a narrow popup breaks before the nickname ("Another Nine - / Steele Creek"), never inside it.
      els.facilityName.textContent = '';
      const brand = document.createElement('span');
      brand.textContent = 'Another Nine - ';
      const nickname = document.createElement('span');
      nickname.className = 'location-confirmation-popup__facility-nickname';
      nickname.textContent = info.facility_name;
      els.facilityName.append(brand, nickname);
      // No address1 fallback: a coming-soon facility's address is left blank on purpose.
      els.facilityAddress.textContent = info.address || '';
      els.facilityAddress.hidden = !els.facilityAddress.textContent;
      if (info.photo) {
        els.facilityPhoto.src = info.photo;
        els.facilityPhoto.hidden = false;
      } else {
        els.facilityPhoto.removeAttribute('src');
        els.facilityPhoto.hidden = true;
      }
    }

    /**
     * "EVENING · FRI, OCT 2" over a large "7:10 PM – 9:10 PM". The part of day comes from the
     * start time via A9DateUtils.partOfDay, its name from A9DateUtils.PART_OF_DAY_LABELS (the
     * booking popup's tab names). Returns false when there is no block to fill, so the plain row shows.
     */
    fillWhen(els, data) {
      if (!els.when || !els.whenTime || !els.whenMeta) return false;
      const start = String(data.time).split(/\s[–-]\s/)[0].trim();
      const dateUtils = window.A9DateUtils;
      const part = dateUtils && dateUtils.partOfDay ? dateUtils.partOfDay(dateUtils.time12hToMinutes(start)) : null;

      els.whenTime.textContent = String(data.time);
      els.whenMeta.textContent = [part ? dateUtils.PART_OF_DAY_LABELS[part].name : '', data.date].filter(Boolean).join(' · ');
      els.whenMeta.hidden = !els.whenMeta.textContent;
      return true;
    }

    fillPopup(data) {
      const els = this.getElements();
      if (!els || !data) return;

      const s = window.locationConfirmationStrings || {};
      const type = data.type || 'reservation';

      els.locationValue.textContent = data.location != null ? String(data.location) : '';
      this.fillFacilityCard(els, data);

      const popupTitles = {
        reservation: s.popup_reservation_title || 'Please confirm your reservation details:',
        membership: s.popup_membership_title || 'Please confirm your subscription details:',
        league_pass: s.popup_league_pass_title || 'Please confirm your League Pass details:',
        league_reservation: s.popup_league_reservation_title || 'Please confirm your League Reservation details:'
      };
      els.popupTitle.textContent = popupTitles[type] || (type === 'league' ? (s.popup_league_pass_title || popupTitles.league_pass) : popupTitles.reservation);

      if (!els.extraRow || !els.extraLabel || !els.extraValue) return;

      const showWhen = type === 'reservation' && !!data.time && this.fillWhen(els, data);
      if (els.when) els.when.hidden = !showWhen;

      if (showWhen) {
        els.extraRow.hidden = true;
      } else if (type === 'reservation' && (data.date || data.time)) {
        els.extraRow.hidden = false;
        els.extraLabel.textContent = s.time || 'Time:';
        els.extraValue.textContent = [data.date, data.time].filter(Boolean).join(' · ');
      } else if (type === 'membership') {
        els.extraRow.hidden = false;
        els.extraLabel.textContent = s.plan || 'Plan:';
        const planDisplay = LocationConfirmationPopup.formatMembershipPlanDisplay(data.plan, data.planKey);
        els.extraValue.textContent = planDisplay || (data.plan ? String(data.plan).trim() : '') || '—';
      } else if ((type === 'league_pass' || type === 'league_reservation' || type === 'league') && data.groupSize != null && data.groupSize !== '') {
        els.extraRow.hidden = false;
        els.extraLabel.textContent = s.group_size || 'Group size:';
        els.extraValue.textContent = data.groupSize + ' ' + (s.players || 'Players');
      } else {
        els.extraRow.hidden = true;
      }
    }

    // ─── Confirm flow: update location (if needed), then run onConfirm; keep popup open with loading until done ───

    async runConfirmFlow(data, onConfirm) {
      if (data.city_state && window.locationDetector && typeof window.locationDetector.updateLocationWithoutRedirect === 'function') {
        const currentCity = (typeof localStorage !== 'undefined' && localStorage.getItem('location')) || '';
        const currentFacility = (typeof localStorage !== 'undefined' && localStorage.getItem('selected_facility')) || '';
        const productCityState = (data.city_state || '').trim();
        const productFacility = (data.facility || '').trim();
        const cityChanged = productCityState && productCityState.toLowerCase() !== (currentCity || '').toLowerCase();
        const facilityChanged = productFacility && productFacility.toLowerCase() !== (currentFacility || '').toLowerCase();
        if (cityChanged || facilityChanged) {
          await window.locationDetector.updateLocationWithoutRedirect(data.city_state, data.facility || null);
        }
      }
      LocationConfirmationPopup.setConfirmedLocation(data.city_state || data.location || '');

      if (typeof onConfirm === 'function') {
        await Promise.resolve(onConfirm());
      }
    }

    /**
     * Remove any listeners from a previous show() so one confirm click cannot run multiple onConfirm callbacks.
     */
    _removeExistingListeners() {
      const els = this.getElements();
      if (!els) return;
      if (this._confirmHandler && els.confirmBtn) {
        els.confirmBtn.removeEventListener('click', this._confirmHandler);
        this._confirmHandler = null;
      }
      if (this._cancelHandler && els.cancelBtn) {
        els.cancelBtn.removeEventListener('click', this._cancelHandler);
        this._cancelHandler = null;
      }
      if (this._overlayHandler && els.popup) {
        els.popup.removeEventListener('click', this._overlayHandler);
        this._overlayHandler = null;
      }
    }

    /**
     * Show location confirmation popup.
     * On confirm: show loading, update location if needed, run onConfirm (e.g. add to cart), then close popup (drawer opens from cart).
     * Re-entry: clears previous confirm/cancel/overlay listeners so rapid repeated show() does not double-fire onConfirm.
     * @param {Object} data - { location, city_state?, facility?, type, date?, time?, plan?, planKey?, groupSize? }
     * @param {Function} onConfirm - async or sync; called after optional location update; popup closes after it completes
     * @param {Function} [onCancel] - called when user cancels/closes
     */
    show(data, onConfirm, onCancel) {
      if (!data || typeof onConfirm !== 'function') return;

      if (!data.location && data.city_state) {
        data = { ...data, location: data.location_display || data.city_state };
      }

      this.fillPopup(data);

      const els = this.getElements();
      if (!els) {
        onConfirm();
        return;
      }

      this._removeExistingListeners();

      let handled = false;

      const handleConfirm = async () => {
        if (handled) return;
        handled = true;
        if (els.confirmBtn) {
          els.confirmBtn.removeEventListener('click', handleConfirm);
          if (els.cancelBtn) els.cancelBtn.removeEventListener('click', handleCancel);
        }
        this.showLoading(els);

        try {
          await this.runConfirmFlow(data, onConfirm);
        } catch (e) {
          this.hideLoading(els);
          if (els.confirmBtn) els.confirmBtn.addEventListener('click', handleConfirm);
          if (els.cancelBtn) els.cancelBtn.addEventListener('click', handleCancel);
          handled = false;
          throw e;
        }

        this.hideLoading(els);
        this.hidePopup();
      };

      const handleCancel = () => {
        if (handled) return;
        handled = true;
        this.hidePopup();
        if (els.confirmBtn) {
          els.confirmBtn.removeEventListener('click', handleConfirm);
          if (els.cancelBtn) els.cancelBtn.removeEventListener('click', handleCancel);
        }
        if (typeof onCancel === 'function') onCancel();
      };

      els.confirmBtn.addEventListener('click', handleConfirm);
      if (els.cancelBtn) els.cancelBtn.addEventListener('click', handleCancel);

      const closeOnOverlay = (e) => {
        if (e.target.closest('.a9-popup__overlay') || e.target.closest('.a9-popup__close')) {
          handleCancel();
          els.popup.removeEventListener('click', closeOnOverlay);
        }
      };

      this._confirmHandler = handleConfirm;
      this._cancelHandler = handleCancel;
      this._overlayHandler = closeOnOverlay;

      els.popup.addEventListener('click', closeOnOverlay);
      this.showPopup();
    }
  }

  const instance = new LocationConfirmationPopup();

  window.LocationConfirmationPopup = {
    show: (data, onConfirm, onCancel) => instance.show(data, onConfirm, onCancel),
    getCurrentLocation: () => LocationConfirmationPopup.getCurrentLocation(),
    getProductLocationFromPage: () => LocationConfirmationPopup.getProductLocationFromPage(),
    clearConfirmedLocation: () => LocationConfirmationPopup.clearConfirmedLocation()
  };
})();
